import * as React from "react";
import { NewAsyncTree, TreeDataNode } from "Components/LibHelper/NewAsyncTree";
import { Loader } from "Components/General/Loader";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { stringSort } from "utils/defaultSort";

export function findInTree(array: Array<API_TreeElement>, id: number): DocumentsTreeviewUI {
  let item: DocumentsTreeviewUI;
  for (let i = 0; i < array.length; i++) {
    if (array[i].id === id) {
      item = array[i];
      break;
    } else if (array[i].subfolders) {
      const found = findInTree(array[i].subfolders, id);
      if (found) {
        item = found;
      }
    }
  }
  return item;
}

interface FolderPickerProps {
  onSelectFolder: (props: { id: number; name: string }) => void;
  selectedFolderId: number;
  minAccessLevel?: API_DOCUMENTFILTER_MINIMUMACCESSLEVEL;
}

/**
 * This component renders Tree component with folder structure of current project.
 * "minAccessLevel" property sets query parameter for back-end to use according filter level.
 */
export const FolderPicker = ({ onSelectFolder, selectedFolderId, minAccessLevel }: FolderPickerProps) => {
  const [data, setData] = React.useState<TreeDataNode>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const loadRootFolder = async () => {
    try {
      setLoading(true);
      const response = await API.getData<API_TreeElement>(API_URL.FOLDERS.getTreeRoot(projectsDataStore.projectId), {
        minAccessLevel,
      });
      setData({
        key: response.id,
        title: response.name,
        isLeaf: !response.subfolders,
        selectable: true,
        children: response.subfolders
          ?.sort((a, b) => stringSort(a, b, "name"))
          .map((item) => ({
            key: item.id,
            isLeaf: !item.children,
            title: item.name,
            selectable: true,
            disabled: item.unselectable,
          })),
      });
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loadChildFolderById = async (folderId: number | string): Promise<TreeDataNode[]> => {
    try {
      const response = await API.getData<API_TreeElement>(
        API_URL.FOLDERS.getSubfolders(projectsDataStore.projectId, folderId),
        { minAccessLevel }
      );
      return response.subfolders
        .sort((a, b) => stringSort(a, b, "name"))
        .map((item) => ({
          key: item.id,
          isLeaf: !item.children,
          title: item.name,
          selectable: true,
          disabled: item.unselectable,
        }));
    } catch (error) {
      throw error;
    }
  };

  const handleSelectFolder = (selectedKeys: string[], info) => {
    const selectedNode = info.selectedNodes[0];
    onSelectFolder({ id: selectedNode.key, name: selectedNode.title });
  };

  // run only on mount
  React.useEffect(() => {
    (async () => await loadRootFolder())();
  }, []);

  if (loading) {
    return <Loader />;
  }

  if (!data) {
    return null;
  }

  return (
    <NewAsyncTree
      initialData={[data]}
      onSelect={handleSelectFolder}
      loadData={loadChildFolderById}
      showLine={false}
      defaultExpandParent={true}
      selectedKeys={[selectedFolderId]}
    />
  );
};
